<style lang="scss" scoped>
.upgrade-banner {
    font-size: 13px;

    ::v-deep .v-alert__border {
        border-width: 1px;
    }

    ::v-deep .v-alert__icon.v-icon {
        align-self: center;
    }
}
</style>

<template>
    <v-row
        v-if="isVisible"
        dense
        no-gutters>
        <v-col v-if="mode === 'banner'">
            <v-alert
                class="upgrade-banner ma-0 pa-0 px-2"
                :border="'bottom'"
                :type="bannerType"
                tile
                text
                dense>
                <div class="text-center py-1">
                    <span class="font-weight-medium">
                        <template v-if="isUpgradeAvailable">
                            {{ $t('infraUpgradeAvailable') }}
                        </template>
                        <template v-else-if="isUpgradeRequired">
                            {{ $t('infraUpgradeRequired') }}
                        </template>
                    </span>
                    <span>{{ $t('upgradeActionInfo', { date: $moment(upgradeDate).format('MMM DD, YYYY, LTS'), }) }}</span>
                    <v-btn
                        class="open-modal txt-compact ml-2"
                        color="primary"
                        height="26"
                        depressed
                        rounded
                        :loading="isInitiateUpgradeInProcess"
                        @click="isConfirmDialogOpen = true">
                        <span>{{ $t('upgrade') }}</span>
                    </v-btn>
                </div>
            </v-alert>
        </v-col>
        <v-tooltip
            v-if="mode === 'button'"
            :disabled="isInitiateUpgradeInProcess"
            open-delay="400"
            max-width="260"
            top>
            <template #activator="{ on }">
                <div v-on="on">
                    <v-btn
                        :color="bannerType"
                        text
                        rounded
                        :loading="isInitiateUpgradeInProcess"
                        @click="isConfirmDialogOpen = true">
                        <v-icon>mdi-update</v-icon>
                        <span class="pl-2">{{ $t('upgrade') }}</span>
                    </v-btn>
                </div>
            </template>
            <div class="text-center">
                <span class="font-weight-medium">
                    <template v-if="isUpgradeAvailable">
                        {{ $t('infraUpgradeAvailable') }}
                    </template>
                    <template v-else-if="isUpgradeRequired">
                        {{ $t('infraUpgradeRequired') }}
                    </template>
                </span>
                <span>{{ $t('upgradeActionInfo', { date: $moment(upgradeDate).format('MMM DD, YYYY, LTS'), }) }}</span>
            </div>
        </v-tooltip>
        <v-dialog
            v-model="isConfirmDialogOpen"
            persistent
            max-width="600px"
            scrollable>
            <v-card>
                <v-card-title class="pt-9 pb-5 px-9">
                    <span class="text-h4 font-weight-medium">
                        {{ $t('confirmation') }}</span>
                </v-card-title>
                <v-card-text class="pb-5 px-9">
                    <span class="text-body-1">
                        {{ $t('warn.infraUpgradeWarningMessage') }}
                    </span>
                    <ul class="my-2">
                        <li>{{ $t('warn.infraUpgradeWarningPointer1') }}</li>
                        <li>{{ $t('warn.infraUpgradeWarningPointer2') }}</li>
                        <li>{{ $t('warn.infraUpgradeWarningPointer3') }}</li>
                        <li>{{ $t('warn.infraUpgradeWarningPointer4') }}</li>
                    </ul>
                    <span class="text-body-1">
                        {{ $t('warn.infraUpgradeWarningConfirmation') }}
                    </span>
                </v-card-text>
                <v-divider />
                <v-card-actions class="py-4 px-9">
                    <v-spacer />
                    <v-btn
                        class="text-none"
                        color="primary"
                        rounded
                        outlined
                        :ripple="false"
                        @click="isConfirmDialogOpen = false">
                        {{ $t('cancel') }}
                    </v-btn>
                    <v-btn
                        class="text-none"
                        color="primary"
                        rounded
                        depressed
                        :ripple="false"
                        @click="onSubmit">
                        {{ $t('confirm') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
    import { mapActions } from 'vuex'

    const InfraUIpgradeBanner = {
        name: 'infra-ui-upgrade-banner',
        props: {
            service: {
                type: Object,
                required: true,
            },
            mode: {
                type: String,
                default: 'banner',
            },
        },
        data() {
            return {
                isConfirmDialogOpen: false,
                isInitiateUpgradeInProcess: false,
            }
        },
        computed: {
            upgradeStatus() {
                return this.service?.upgrades?.kubernetes_upgrade_status
            },
            upgradeDate() {
                return this.service?.upgrades?.kubernetes_upgrade_deadline ?
                    this.$help.normalizeDate(this.service?.upgrades?.kubernetes_upgrade_deadline) : ''
            },
            isUpgradeAvailable() {
                const { AVAILABLE, } = this.$config.kubernetesUpgradeStatus
                return this.upgradeStatus === AVAILABLE
            },
            isUpgradeRequired() {
                const { REQUIRED, } = this.$config.kubernetesUpgradeStatus
                return this.upgradeStatus === REQUIRED
            },
            isVisible() {
                const { READY, } = this.$config.serviceStatus
                if (this.service?.status !== READY) {
                    return false
                }
                // Show banner if upgrade available or required
                if (this.isUpgradeAvailable || this.isUpgradeRequired) {
                    return true
                }
                return false
            },
            bannerType() {
                if (this.isUpgradeAvailable) return 'warning'
                if (this.isUpgradeRequired) return 'error'
                return 'info'
            },
        },
        methods: {
            ...mapActions(['upgrade']),
            async onSubmit() {
                this.isInitiateUpgradeInProcess = true
                this.isConfirmDialogOpen = false
                await this.upgrade({serviceId: this.service.id,})
                this.isInitiateUpgradeInProcess = false
            },
        },
    }

    export default InfraUIpgradeBanner
</script>
